type SwymStoredProduct = {
  sku: string;
  variantId: string | null;
};

type SwymWishlistStorage = {
  all?: unknown;
};

type SwymWishlistRawItem = {
  sku?: string;
  epi?: string;
};

const WISHLIST_ITEM_SELECTOR = ".swym-wishlist-item";
const WISHLIST_IMAGE_LINK_SELECTOR = ".swym-wishlist-image-wrapper";
const WISHLIST_VARIANT_TITLE_SELECTOR = ".swym-variant-title";
const WISHLIST_SKU_BOUND_KEY = "wishlistSkuBound";
const SWYM_WISHLIST_PRODUCTS_KEY = "swym-products";

let cachedWishlistProducts: SwymStoredProduct[] | null | undefined;

const isObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null;

const isSwymWishlistStorage = (value: unknown): value is SwymWishlistStorage =>
  isObject(value) && "all" in value;

const isSwymWishlistRawItem = (value: unknown): value is SwymWishlistRawItem =>
  isObject(value);

const readVariantId = (value: unknown): string | null => {
  if (value == null) return null;
  if (typeof value === "number") return String(value);
  if (typeof value === "string") {
    const trimmed = value.trim();
    return trimmed || null;
  }
  return null;
};

const loadWishlistProducts = (): SwymStoredProduct[] | null => {
  if (cachedWishlistProducts !== undefined) return cachedWishlistProducts;

  if (typeof window === "undefined" || !window.localStorage) {
    return (cachedWishlistProducts = null);
  }

  const raw = window.localStorage.getItem(SWYM_WISHLIST_PRODUCTS_KEY);
  if (!raw) return (cachedWishlistProducts = null);

  try {
    const parsed: unknown = JSON.parse(raw);
    if (!isSwymWishlistStorage(parsed) || !Array.isArray(parsed.all)) {
      return (cachedWishlistProducts = null);
    }

    const products = parsed.all.reduce<SwymStoredProduct[]>((acc, item) => {
      if (!isSwymWishlistRawItem(item) || typeof item.sku !== "string") return acc;

      const sku = item.sku.trim();
      if (!sku) return acc;

      acc.push({ sku, variantId: readVariantId(item.epi) });
      return acc;
    }, []);

    return (cachedWishlistProducts = products.length ? products : null);
  } catch {
    return (cachedWishlistProducts = null);
  }
};

const getVariantIdFromUrl = (url: URL): string | null => {
  const value = url.searchParams.get("variant")?.trim() ?? "";
  return value || null;
};

const findSkuForVariant = (
  variantId: string | null,
): SwymStoredProduct | null => {
  if (!variantId) return null;

  const products = loadWishlistProducts();
  if (!products) return null;

  return products.find((product) => product.variantId === variantId) ?? null;
};

const enhanceWishlistItem = (itemElement: HTMLElement): void => {
  if (itemElement.dataset[WISHLIST_SKU_BOUND_KEY] === "true") return;

  const linkElement = itemElement.querySelector(WISHLIST_IMAGE_LINK_SELECTOR);
  const skuElement = itemElement.querySelector(WISHLIST_VARIANT_TITLE_SELECTOR);

  if (!(linkElement instanceof HTMLAnchorElement)) return;
  if (!(skuElement instanceof HTMLElement)) return;

  const existingText = skuElement.textContent?.trim() || "";

  let url: URL;
  try {
    url = new URL(linkElement.href, window.location.origin);
  } catch {
    return;
  }

  const fromSwym = findSkuForVariant(getVariantIdFromUrl(url));
  if (!fromSwym) return;

  const { sku, variantId } = fromSwym;

  if (variantId) itemElement.dataset.variantId = variantId;
  itemElement.dataset.sku = sku;
  itemElement.dataset[WISHLIST_SKU_BOUND_KEY] = "true";

  const container = document.getElementById("swym-wishlist-render-container");
  const label =
    container instanceof HTMLElement
      ? container.dataset.labelSku?.trim() || null
      : null;

  const skuText = label ? `${label}: ${sku}` : sku;
  skuElement.textContent = existingText
    ? `${existingText} · ${skuText}`
    : skuText;
};

const enhanceWishlistIn = (rootNode: ParentNode): void => {
  rootNode.querySelectorAll(WISHLIST_ITEM_SELECTOR).forEach((element) => {
    if (element instanceof HTMLElement) enhanceWishlistItem(element);
  });
};

const observeWishlist = (): void => {
  const container = document.getElementById("swym-wishlist-render-container");
  if (!(container instanceof HTMLElement)) return;

  const observer = new MutationObserver(() => {
    enhanceWishlistIn(container);
  });

  observer.observe(container, { childList: true, subtree: true });
};

const handleWishlistItemClick = (event: MouseEvent): void => {
  const container = document.getElementById("swym-wishlist-render-container");
  if (!(container instanceof HTMLElement)) return;

  const target = event.target;
  if (!(target instanceof Element)) return;
  if (!container.contains(target)) return;

  const button = target.closest("button");
  if (
    button &&
    button.matches(
      ".swym-delete-btn, .swym-add-to-cart-btn, .swym-wishlist-add-to-cart-btn",
    )
  ) {
    return;
  }

  const itemElement = target.closest<HTMLElement>(WISHLIST_ITEM_SELECTOR);
  if (!(itemElement instanceof HTMLElement)) return;

  const imageLink = itemElement.querySelector<HTMLAnchorElement>(
    WISHLIST_IMAGE_LINK_SELECTOR,
  );
  if (!(imageLink instanceof HTMLAnchorElement)) return;

  event.preventDefault();
  event.stopPropagation();
  event.stopImmediatePropagation();

  window.location.href = imageLink.href;
};

const initWishlistNavigation = (): void => {
  document.addEventListener("click", handleWishlistItemClick, true);
};

enhanceWishlistIn(document);
observeWishlist();
initWishlistNavigation();
